/**
 * AssetDetailPanel — slide-in panel from right for editing asset metadata.
 *
 * Shows preview, editable metadata form, file info, and activity timeline.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { X, Star, Save, Cpu, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react'
import type { Asset, EditorialStatus, Priority, ColorLabel } from '../../types/fileManager'
import { isVideoAsset } from '../../utils/mediaType'
import { assetApi } from '../../services/assetFolderApi'
import apiClient from '../../services/api'
import TagInput from './TagInput'
import StatusBadge from './StatusBadge'
import AssetActivityTimeline from './AssetActivityTimeline'
import AssetPreview from './AssetPreview'
import AssetPreviewModal from './AssetPreviewModal'
import toast from 'react-hot-toast'
import { apiErrorDetail } from '../../utils/dataValidator'

interface AssetDetailPanelProps {
  asset: Asset
  onClose: () => void
  onAssetUpdated: (updated: Asset) => void
  allTags?: string[]
}

const STATUS_OPTIONS: { value: EditorialStatus; label: string }[] = [
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'selects', label: 'Selects' },
  { value: 'rejected', label: 'Rejected' },
]

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'p1', label: 'P1 — High' },
  { value: 'p2', label: 'P2 — Medium' },
  { value: 'p3', label: 'P3 — Low' },
]

const COLOR_OPTIONS: { value: ColorLabel; color: string }[] = [
  { value: 'red', color: 'bg-red-500' },
  { value: 'orange', color: 'bg-orange-500' },
  { value: 'yellow', color: 'bg-yellow-500' },
  { value: 'green', color: 'bg-emerald-500' },
  { value: 'blue', color: 'bg-blue-500' },
  { value: 'purple', color: 'bg-purple-500' },
]

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export default function AssetDetailPanel({ asset, onClose, onAssetUpdated, allTags = [] }: AssetDetailPanelProps) {
  const [tags, setTags] = useState<string[]>(asset.tags || [])
  const [notes, setNotes] = useState(asset.notes || '')
  const [editorialStatus, setEditorialStatus] = useState(asset.editorial_status || 'needs_review')
  const [priority, setPriority] = useState(asset.priority || '')
  const [colorLabel, setColorLabel] = useState(asset.color_label || '')
  const [rating, setRating] = useState(asset.rating || 0)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  // Ingest state
  const [analyzing, setAnalyzing] = useState(false)
  const [ingestStep, setIngestStep] = useState('')
  const [ingestProgress, setIngestProgress] = useState(0)
  const [momentsCount, setMomentsCount] = useState<number | null>(null)
  const isVideo = isVideoAsset(asset)

  // Reset form when the asset *identity* changes, and only then. The parent
  // re-renders this panel with a fresh `asset` object after every save and
  // every poll tick; resetting on those would wipe whatever the user has typed
  // since. The dependency list is narrower than the effect body on purpose. The directive sits on the dependency line, where the rule
  // reports, not on the hook's opening line.
  useEffect(() => {
    setTags(asset.tags || [])
    setNotes(asset.notes || '')
    setEditorialStatus(asset.editorial_status || 'needs_review')
    setPriority(asset.priority || '')
    setColorLabel(asset.color_label || '')
    setRating(asset.rating || 0)
    setDirty(false)
    setShowPreviewModal(false)
  }, [asset.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // The polling effect below reads `asset` and `onAssetUpdated` but must not
  // restart its interval when they change: the parent hands down a new object
  // and a new callback on every render, and a 2 s timer that is reset more often
  // than every 2 s never fires. Latest values live in refs; the effect keys on
  // the fields that actually decide whether to poll.
  const assetRef = useRef(asset)
  const onAssetUpdatedRef = useRef(onAssetUpdated)
  useEffect(() => {
    assetRef.current = asset
    onAssetUpdatedRef.current = onAssetUpdated
  })

  // Fetch moments count for already-analyzed assets
  useEffect(() => {
    if (!isVideo || asset.status !== 'ready') { setMomentsCount(null); return }
    let cancelled = false
    apiClient.get(`/ingest/${asset.id}/status`).then(res => {
      if (!cancelled) setMomentsCount(res.data.moments_count ?? null)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [asset.id, asset.status, isVideo])

  // Poll ingest status when asset is ingesting
  useEffect(() => {
    if (!isVideo || asset.status !== 'ingesting') {
      setIngestStep('')
      setIngestProgress(0)
      return
    }
    let cancelled = false
    const poll = setInterval(async () => {
      try {
        const res = await apiClient.get(`/ingest/${asset.id}/status`)
        if (cancelled) return
        setIngestStep(res.data.step || '')
        setIngestProgress(res.data.progress || 0)
        if (res.data.status === 'ready' || res.data.status === 'failed') {
          clearInterval(poll)
          setMomentsCount(res.data.moments_count ?? null)
          onAssetUpdatedRef.current({ ...assetRef.current, status: res.data.status })
          if (res.data.status === 'ready') toast.success(`Analysis complete — ${res.data.moments_count} shots detected`)
          if (res.data.status === 'failed') toast.error('Analysis failed')
        }
      } catch { /* ignore polling errors */ }
    }, 2000)
    return () => { cancelled = true; clearInterval(poll) }
  }, [asset.id, asset.status, isVideo])

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true)
    try {
      await apiClient.post(`/ingest/${asset.id}/start`)
      onAssetUpdated({ ...asset, status: 'ingesting' })
      toast.success('Analysis started')
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to start analysis'))
    } finally {
      setAnalyzing(false)
    }
  }, [asset, onAssetUpdated])

  const handleRetry = useCallback(async () => {
    setAnalyzing(true)
    try {
      await apiClient.post(`/ingest/${asset.id}/retry`)
      onAssetUpdated({ ...asset, status: 'ingesting' })
      toast.success('Re-analysis started')
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to retry analysis'))
    } finally {
      setAnalyzing(false)
    }
  }, [asset, onAssetUpdated])

  // Track dirty state
  const markDirty = useCallback(() => setDirty(true), [])

  const handleSave = useCallback(async () => {
    if (!dirty) return
    setSaving(true)
    try {
      const data: Record<string, unknown> = {}
      if (JSON.stringify(tags) !== JSON.stringify(asset.tags || [])) data.tags = tags
      if (notes !== (asset.notes || '')) data.notes = notes
      if (editorialStatus !== (asset.editorial_status || 'needs_review')) data.editorial_status = editorialStatus
      if (priority !== (asset.priority || '')) data.priority = priority || null
      if (colorLabel !== (asset.color_label || '')) data.color_label = colorLabel || null
      if (rating !== (asset.rating || 0)) data.rating = rating || null

      if (Object.keys(data).length > 0) {
        const updated = await assetApi.update(asset.id, data)
        onAssetUpdated(updated)
        toast.success('Asset updated')
      }
      setDirty(false)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to update'))
    } finally {
      setSaving(false)
    }
  }, [dirty, tags, notes, editorialStatus, priority, colorLabel, rating, asset, onAssetUpdated])

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="fm-detail-panel w-80 flex-shrink-0 border-l border-white/10 bg-[var(--rq-surface)] flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--rq-text-soft)]">Details</span>
        <div className="flex items-center gap-1">
          {dirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="p-1 rounded hover:bg-white/10 text-emerald-400 hover:text-emerald-300 transition-colors"
              title="Save changes"
            >
              <Save className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Preview */}
        <div className="flex flex-col items-center py-4 px-4 border-b border-white/5 gap-2">
          <AssetPreview asset={asset} compact onExpand={() => setShowPreviewModal(true)} />
          <p className="text-sm text-[var(--rq-text)] text-center font-medium break-all">{asset.original_filename}</p>
          <StatusBadge status={editorialStatus} priority={priority || undefined} size="md" />
        </div>

        {/* Ingest / Analyze section (video assets only) */}
        {isVideo && (
          <div className="px-4 py-3 border-b border-white/5 space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] block">Analysis</label>

            {asset.status === 'uploaded' && (
              <div className="space-y-2">
                <button
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
                >
                  <Cpu className="w-3.5 h-3.5" />
                  {analyzing ? 'Starting...' : 'Analyze Video'}
                </button>
                <p className="text-[10px] text-[var(--rq-text-soft)] leading-relaxed">
                  Detects shots, extracts audio/motion signals, and transcribes speech. Required before creating an AI draft.
                </p>
              </div>
            )}

            {asset.status === 'ingesting' && (
              <div className="space-y-2">
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px]">
                    <span className="text-amber-300 capitalize">{ingestStep.replace(/_/g, ' ') || 'Queued'}</span>
                    <span className="text-[var(--rq-text-soft)] tabular-nums">{ingestProgress}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all duration-500"
                      style={{ width: `${ingestProgress}%` }}
                    />
                  </div>
                </div>
                <p className="text-[10px] text-[var(--rq-text-soft)] leading-relaxed">
                  Analyzing video — detecting shots, extracting signals, and transcribing speech. This may take a minute.
                </p>
              </div>
            )}

            {asset.status === 'ready' && (
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  Analyzed{momentsCount != null ? ` — ${momentsCount} shot${momentsCount !== 1 ? 's' : ''} detected` : ''}
                </span>
              </div>
            )}

            {asset.status === 'failed' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-red-400">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Analysis failed</span>
                  </div>
                  <button
                    onClick={handleRetry}
                    disabled={analyzing}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Retry
                  </button>
                </div>
                <p className="text-[10px] text-[var(--rq-text-soft)] leading-relaxed">
                  Shot detection or signal extraction encountered an error. Check that the video format is supported and try again.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Metadata form */}
        <div className="px-4 py-4 space-y-4 border-b border-white/5">
          {/* Status */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Status</label>
            <select
              value={editorialStatus}
              onChange={e => { setEditorialStatus(e.target.value); markDirty() }}
              className="w-full rq-input-dark text-xs py-1.5"
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Priority</label>
            <select
              value={priority}
              onChange={e => { setPriority(e.target.value); markDirty() }}
              className="w-full rq-input-dark text-xs py-1.5"
            >
              <option value="">None</option>
              {PRIORITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Rating */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => { setRating(rating === star ? 0 : star); markDirty() }}
                  className="transition-colors"
                >
                  <Star
                    className={`w-4 h-4 ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-white/20'}`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Color label */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Color Label</label>
            <div className="flex gap-2">
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c.value}
                  onClick={() => { setColorLabel(colorLabel === c.value ? '' : c.value); markDirty() }}
                  className={`w-5 h-5 rounded-full ${c.color} transition-all ${
                    colorLabel === c.value ? 'ring-2 ring-white/60 ring-offset-1 ring-offset-[var(--rq-surface)]' : 'opacity-50 hover:opacity-80'
                  }`}
                  title={c.value}
                />
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Tags</label>
            <TagInput
              tags={tags}
              onChange={t => { setTags(t); markDirty() }}
              suggestions={allTags}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Notes</label>
            <textarea
              value={notes}
              onChange={e => { setNotes(e.target.value); markDirty() }}
              placeholder="Add notes..."
              rows={3}
              className="w-full rq-input-dark text-xs py-1.5 resize-none"
            />
          </div>
        </div>

        {/* File info */}
        <div className="px-4 py-4 space-y-2 border-b border-white/5">
          <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-2 block">File Info</label>
          <div className="grid grid-cols-2 gap-y-1.5 text-xs">
            <span className="text-[var(--rq-text-soft)]">Type</span>
            <span className="text-[var(--rq-text)] text-right">{asset.file_type.split('/').pop()}</span>
            <span className="text-[var(--rq-text-soft)]">Size</span>
            <span className="text-[var(--rq-text)] text-right">{formatFileSize(asset.file_size)}</span>
            <span className="text-[var(--rq-text-soft)]">Uploaded</span>
            <span className="text-[var(--rq-text)] text-right">
              {asset.upload_time ? new Date(asset.upload_time).toLocaleDateString() : '—'}
            </span>
            <span className="text-[var(--rq-text-soft)]">Folder</span>
            <span className="text-[var(--rq-text)] text-right truncate" title={asset.folder_path || undefined}>
              {asset.folder_path?.split('/').pop() || '—'}
            </span>
            {asset.duration != null && (
              <>
                <span className="text-[var(--rq-text-soft)]">Duration</span>
                <span className="text-[var(--rq-text)] text-right">{Math.round(asset.duration)}s</span>
              </>
            )}
          </div>
        </div>

        {/* Activity timeline */}
        <div className="px-4 py-4">
          <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-2 block">Activity</label>
          <AssetActivityTimeline assetId={asset.id} />
        </div>
      </div>

      {/* Save bar (sticky) */}
      {dirty && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-[var(--rq-surface)]">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full rq-btn-primary py-2 text-xs justify-center"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Fullscreen preview modal */}
      {showPreviewModal && (
        <AssetPreviewModal asset={asset} onClose={() => setShowPreviewModal(false)} />
      )}
    </div>
  )
}
